/**
 * The Phase 2 operations: teams, branches, protection rules, the pull request
 * and issue additions, labels, milestones, projects, the inbox, search, the
 * dashboard and the audit log.
 *
 * Split from `operations.ts` for length and re-exported from it, exactly as
 * `repo.ts` is. The rules are the same: one function per `/v1` operation,
 * paths checked against the generated contract, and the collection envelope
 * normalised in one place.
 */

import { expect, expectNoContent, http } from './client';
import type { components } from './gen/schema';
import type { Paged } from './repo';

type Schemas = components['schemas'];

export type Team = Schemas['Team'];
export type TeamMember = Schemas['TeamMember'];
export type TeamRole = Schemas['TeamRole'];
export type TeamGrant = Schemas['TeamGrant'];
export type BranchProtectionRule = Schemas['BranchProtectionRule'];
export type PullRequestChecks = Schemas['PullRequestChecks'];
export type CommitCheck = Schemas['CommitCheck'];
export type MergeBlocker = Schemas['MergeBlocker'];
export type LinkedIssue = Schemas['LinkedIssue'];
export type LinkedPullRequest = Schemas['LinkedPullRequest'];
export type Label = Schemas['Label'];
export type Milestone = Schemas['Milestone'];
export type IssuePriority = Schemas['IssuePriority'];
export type Project = Schemas['Project'];
export type ProjectStatus = Schemas['ProjectStatus'];
export type ProjectStatusCategory = Schemas['ProjectStatusCategory'];
export type ProjectItem = Schemas['ProjectItem'];
export type SearchHit = Schemas['SearchHit'];
export type SearchType = Schemas['SearchType'];
export type Dashboard = Schemas['Dashboard'];
export type AuditEvent = Schemas['AuditEvent'];
export type AuditOutcome = Schemas['AuditOutcome'];
export type TimelineEntry = Schemas['ActivityEntry'];
export type NotificationPreference = Schemas['NotificationPreference'];

function paged<T>(value: { data?: unknown; next_cursor?: unknown }): Paged<T> {
  return {
    data: (value.data ?? []) as T[],
    next_cursor: typeof value.next_cursor === 'string' ? value.next_cursor : null,
  };
}

interface RepoScope {
  orgId: string;
  repoId: string;
}

const repoPath = ({ orgId, repoId }: RepoScope) => ({ org_id: orgId, repo_id: repoId });
const numbered = (scope: RepoScope, number: number) => ({ ...repoPath(scope), number });

/** `{ cursor }` only when there is one: the contract's parameter is optional, not nullable. */
const at = (cursor: string | undefined) => (cursor ? { cursor } : {});

/* ====================================================================== *
 * Teams
 * ====================================================================== */

export async function listTeams(orgId: string): Promise<Paged<Team>> {
  return paged<Team>(
    await expect(
      http.GET('/organizations/{org_id}/teams', {
        params: { path: { org_id: orgId }, query: { limit: 100 } },
      }),
    ),
  );
}

export function createTeam(
  orgId: string,
  body: { slug: string; name: string; description?: string },
): Promise<Team> {
  return expect(
    http.POST('/organizations/{org_id}/teams', { params: { path: { org_id: orgId } }, body }),
  );
}

export function deleteTeam(orgId: string, teamId: string): Promise<void> {
  return expectNoContent(
    http.DELETE('/organizations/{org_id}/teams/{team_id}', {
      params: { path: { org_id: orgId, team_id: teamId } },
    }),
  );
}

export async function listTeamMembers(orgId: string, teamId: string): Promise<Paged<TeamMember>> {
  return paged<TeamMember>(
    await expect(
      http.GET('/organizations/{org_id}/teams/{team_id}/members', {
        params: { path: { org_id: orgId, team_id: teamId }, query: { limit: 100 } },
      }),
    ),
  );
}

export function setTeamMember(
  orgId: string,
  teamId: string,
  userId: string,
  role: TeamRole,
): Promise<TeamMember> {
  return expect(
    http.PUT('/organizations/{org_id}/teams/{team_id}/members/{user_id}', {
      params: { path: { org_id: orgId, team_id: teamId, user_id: userId } },
      body: { role },
    }),
  );
}

export function removeTeamMember(orgId: string, teamId: string, userId: string): Promise<void> {
  return expectNoContent(
    http.DELETE('/organizations/{org_id}/teams/{team_id}/members/{user_id}', {
      params: { path: { org_id: orgId, team_id: teamId, user_id: userId } },
    }),
  );
}

export async function listRepositoryTeams(scope: RepoScope): Promise<Paged<TeamGrant>> {
  return paged<TeamGrant>(
    await expect(
      http.GET('/organizations/{org_id}/repositories/{repo_id}/teams', {
        params: { path: repoPath(scope) },
      }),
    ),
  );
}

export function setRepositoryTeam(
  scope: RepoScope,
  teamId: string,
  role: Schemas['RepoRole'],
): Promise<TeamGrant> {
  return expect(
    http.PUT('/organizations/{org_id}/repositories/{repo_id}/teams/{team_id}', {
      params: { path: { ...repoPath(scope), team_id: teamId } },
      body: { role },
    }),
  );
}

export function removeRepositoryTeam(scope: RepoScope, teamId: string): Promise<void> {
  return expectNoContent(
    http.DELETE('/organizations/{org_id}/repositories/{repo_id}/teams/{team_id}', {
      params: { path: { ...repoPath(scope), team_id: teamId } },
    }),
  );
}

/* ====================================================================== *
 * Branches and protection
 * ====================================================================== */

/**
 * One page of branches, by name.
 *
 * The list is paginated since Phase 2; `listBranches` in `repo.ts` reads the
 * first page for the pickers, and the Branches page walks the rest with this.
 */
export async function listBranchPage(
  scope: RepoScope,
  options: { cursor?: string; q?: string; limit?: number } = {},
): Promise<Paged<Schemas['Branch']>> {
  return paged<Schemas['Branch']>(
    await expect(
      http.GET('/organizations/{org_id}/repositories/{repo_id}/branches', {
        params: {
          path: repoPath(scope),
          query: {
            limit: options.limit ?? 50,
            ...at(options.cursor),
            ...(options.q ? { q: options.q } : {}),
          },
        },
      }),
    ),
  );
}

export function createBranch(
  scope: RepoScope,
  body: { name: string; from?: string },
): Promise<Schemas['Branch']> {
  return expect(
    http.POST('/organizations/{org_id}/repositories/{repo_id}/branches', {
      params: { path: repoPath(scope) },
      body,
    }),
  );
}

/**
 * Delete a branch that still points where the caller saw it pointing.
 *
 * `expectedCommit` is required by the server: a branch somebody pushed to a
 * moment ago is not the branch the person decided to delete.
 */
export function deleteBranch(
  scope: RepoScope,
  name: string,
  expectedCommit: string,
): Promise<void> {
  return expectNoContent(
    http.DELETE('/organizations/{org_id}/repositories/{repo_id}/branches', {
      params: { path: repoPath(scope), query: { name, expected_commit: expectedCommit } },
    }),
  );
}

export async function listBranchProtectionRules(
  scope: RepoScope,
): Promise<Paged<BranchProtectionRule>> {
  return paged<BranchProtectionRule>(
    await expect(
      http.GET('/organizations/{org_id}/repositories/{repo_id}/protection-rules', {
        params: { path: repoPath(scope) },
      }),
    ),
  );
}

export function createBranchProtectionRule(
  scope: RepoScope,
  body: {
    pattern: string;
    require_pull_request?: boolean;
    required_approvals?: number;
    dismiss_stale_approvals?: boolean;
    require_conversation_resolution?: boolean;
    require_up_to_date?: boolean;
    allow_force_pushes?: boolean;
    allow_deletions?: boolean;
    required_checks?: string[];
  },
): Promise<BranchProtectionRule> {
  return expect(
    http.POST('/organizations/{org_id}/repositories/{repo_id}/protection-rules', {
      params: { path: repoPath(scope) },
      body,
    }),
  );
}

export function deleteBranchProtectionRule(scope: RepoScope, ruleId: string): Promise<void> {
  return expectNoContent(
    http.DELETE('/organizations/{org_id}/repositories/{repo_id}/protection-rules/{rule_id}', {
      params: { path: { ...repoPath(scope), rule_id: ruleId } },
    }),
  );
}

/* ====================================================================== *
 * Pull requests: checks, timeline, links, reviewers
 * ====================================================================== */

export function listPullRequestChecks(
  scope: RepoScope,
  number: number,
): Promise<PullRequestChecks> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/pull-requests/{number}/checks', {
      params: { path: numbered(scope, number) },
    }),
  );
}

export async function listPullRequestTimeline(
  scope: RepoScope,
  number: number,
): Promise<Paged<TimelineEntry>> {
  return paged<TimelineEntry>(
    await expect(
      http.GET('/organizations/{org_id}/repositories/{repo_id}/pull-requests/{number}/timeline', {
        params: { path: numbered(scope, number), query: { limit: 100 } },
      }),
    ),
  );
}

export async function listPullRequestLinkedIssues(
  scope: RepoScope,
  number: number,
): Promise<LinkedIssue[]> {
  return paged<LinkedIssue>(
    await expect(
      http.GET(
        '/organizations/{org_id}/repositories/{repo_id}/pull-requests/{number}/linked-issues',
        { params: { path: numbered(scope, number) } },
      ),
    ),
  ).data;
}

export function removePullRequestReviewer(
  scope: RepoScope,
  number: number,
  userId: string,
): Promise<void> {
  return expectNoContent(
    http.DELETE(
      '/organizations/{org_id}/repositories/{repo_id}/pull-requests/{number}/reviewers/{user_id}',
      { params: { path: { ...numbered(scope, number), user_id: userId } } },
    ),
  );
}

/* ====================================================================== *
 * Issues: filing, timeline, links
 * ====================================================================== */

export interface IssueQuery {
  state?: 'open' | 'closed';
  priority?: IssuePriority;
  sort?: 'newest' | 'oldest' | 'recently_updated' | 'priority';
  q?: string;
  assigneeId?: string;
  labelId?: string;
  milestoneId?: string;
  cursor?: string;
  limit?: number;
}

export async function searchIssues(
  scope: RepoScope,
  query: IssueQuery,
): Promise<Paged<Schemas['Issue']>> {
  return paged<Schemas['Issue']>(
    await expect(
      http.GET('/organizations/{org_id}/repositories/{repo_id}/issues', {
        params: {
          path: repoPath(scope),
          query: {
            limit: query.limit ?? 30,
            ...(query.state ? { state: query.state } : {}),
            ...(query.priority ? { priority: query.priority } : {}),
            ...(query.sort ? { sort: query.sort } : {}),
            ...(query.q ? { q: query.q } : {}),
            ...(query.assigneeId ? { assignee_id: query.assigneeId } : {}),
            ...(query.labelId ? { label_id: query.labelId } : {}),
            ...(query.milestoneId ? { milestone_id: query.milestoneId } : {}),
            ...at(query.cursor),
          },
        },
      }),
    ),
  );
}

export function setIssuePriority(
  scope: RepoScope,
  number: number,
  priority: IssuePriority,
  ifVersion: number,
): Promise<Schemas['Issue']> {
  return expect(
    http.PUT('/organizations/{org_id}/repositories/{repo_id}/issues/{number}/priority', {
      params: { path: numbered(scope, number) },
      body: { priority, if_version: ifVersion },
    }),
  );
}

export function setIssueLabels(
  scope: RepoScope,
  number: number,
  labelIds: string[],
  ifVersion: number,
): Promise<Schemas['Issue']> {
  return expect(
    http.PUT('/organizations/{org_id}/repositories/{repo_id}/issues/{number}/labels', {
      params: { path: numbered(scope, number) },
      body: { label_ids: labelIds, if_version: ifVersion },
    }),
  );
}

export function setIssueAssignees(
  scope: RepoScope,
  number: number,
  assigneeIds: string[],
  ifVersion: number,
): Promise<Schemas['Issue']> {
  return expect(
    http.PUT('/organizations/{org_id}/repositories/{repo_id}/issues/{number}/assignees', {
      params: { path: numbered(scope, number) },
      body: { assignee_ids: assigneeIds, if_version: ifVersion },
    }),
  );
}

export function setIssueMilestone(
  scope: RepoScope,
  number: number,
  milestoneId: string,
  ifVersion: number,
): Promise<Schemas['Issue']> {
  return expect(
    http.PUT('/organizations/{org_id}/repositories/{repo_id}/issues/{number}/milestone', {
      params: { path: numbered(scope, number) },
      body: { milestone_id: milestoneId, if_version: ifVersion },
    }),
  );
}

export function clearIssueMilestone(scope: RepoScope, number: number): Promise<Schemas['Issue']> {
  return expect(
    http.DELETE('/organizations/{org_id}/repositories/{repo_id}/issues/{number}/milestone', {
      params: { path: numbered(scope, number) },
    }),
  );
}

export async function listIssueTimeline(
  scope: RepoScope,
  number: number,
): Promise<Paged<TimelineEntry>> {
  return paged<TimelineEntry>(
    await expect(
      http.GET('/organizations/{org_id}/repositories/{repo_id}/issues/{number}/timeline', {
        params: { path: numbered(scope, number), query: { limit: 100 } },
      }),
    ),
  );
}

export async function listIssueLinkedPullRequests(
  scope: RepoScope,
  number: number,
): Promise<LinkedPullRequest[]> {
  return paged<LinkedPullRequest>(
    await expect(
      http.GET(
        '/organizations/{org_id}/repositories/{repo_id}/issues/{number}/linked-pull-requests',
        { params: { path: numbered(scope, number) } },
      ),
    ),
  ).data;
}

/* ====================================================================== *
 * Labels and milestones
 * ====================================================================== */

export async function listLabels(scope: RepoScope): Promise<Label[]> {
  return paged<Label>(
    await expect(
      http.GET('/organizations/{org_id}/repositories/{repo_id}/labels', {
        params: { path: repoPath(scope), query: { limit: 100 } },
      }),
    ),
  ).data;
}

export function createLabel(
  scope: RepoScope,
  body: { name: string; color: string; description?: string },
): Promise<Label> {
  return expect(
    http.POST('/organizations/{org_id}/repositories/{repo_id}/labels', {
      params: { path: repoPath(scope) },
      body,
    }),
  );
}

export function deleteLabel(scope: RepoScope, labelId: string): Promise<void> {
  return expectNoContent(
    http.DELETE('/organizations/{org_id}/repositories/{repo_id}/labels/{label_id}', {
      params: { path: { ...repoPath(scope), label_id: labelId } },
    }),
  );
}

export async function listMilestones(scope: RepoScope): Promise<Milestone[]> {
  return paged<Milestone>(
    await expect(
      http.GET('/organizations/{org_id}/repositories/{repo_id}/milestones', {
        params: { path: repoPath(scope), query: { limit: 100 } },
      }),
    ),
  ).data;
}

export function createMilestone(
  scope: RepoScope,
  body: { title: string; description?: string; due_on?: string },
): Promise<Milestone> {
  return expect(
    http.POST('/organizations/{org_id}/repositories/{repo_id}/milestones', {
      params: { path: repoPath(scope) },
      body,
    }),
  );
}

export function updateMilestone(
  scope: RepoScope,
  milestoneId: string,
  body: { state?: Schemas['MilestoneState']; title?: string; if_version?: number },
): Promise<Milestone> {
  return expect(
    http.PATCH('/organizations/{org_id}/repositories/{repo_id}/milestones/{milestone_id}', {
      params: { path: { ...repoPath(scope), milestone_id: milestoneId } },
      body,
    }),
  );
}

/* ====================================================================== *
 * Projects
 * ====================================================================== */

const projectPath = (orgId: string, projectId: string) => ({
  org_id: orgId,
  project_id: projectId,
});

export async function listProjects(
  orgId: string,
  options: {
    state?: Schemas['ProjectState'];
    repositoryId?: string;
    issueId?: string;
    cursor?: string;
  } = {},
): Promise<Paged<Project>> {
  return paged<Project>(
    await expect(
      http.GET('/organizations/{org_id}/projects', {
        params: {
          path: { org_id: orgId },
          query: {
            limit: 50,
            ...(options.state ? { state: options.state } : {}),
            ...(options.repositoryId ? { repository_id: options.repositoryId } : {}),
            ...(options.issueId ? { issue_id: options.issueId } : {}),
            ...at(options.cursor),
          },
        },
      }),
    ),
  );
}

export function createProject(
  orgId: string,
  body: { name: string; description?: string },
): Promise<Project> {
  return expect(
    http.POST('/organizations/{org_id}/projects', { params: { path: { org_id: orgId } }, body }),
  );
}

export function getProject(orgId: string, projectId: string): Promise<Project> {
  return expect(
    http.GET('/organizations/{org_id}/projects/{project_id}', {
      params: { path: projectPath(orgId, projectId) },
    }),
  );
}

export function updateProject(
  orgId: string,
  projectId: string,
  body: {
    name?: string;
    description?: string;
    state?: Schemas['ProjectState'];
    if_version?: number;
  },
): Promise<Project> {
  return expect(
    http.PATCH('/organizations/{org_id}/projects/{project_id}', {
      params: { path: projectPath(orgId, projectId) },
      body,
    }),
  );
}

export function deleteProject(orgId: string, projectId: string): Promise<void> {
  return expectNoContent(
    http.DELETE('/organizations/{org_id}/projects/{project_id}', {
      params: { path: projectPath(orgId, projectId) },
    }),
  );
}

export function createProjectStatus(
  orgId: string,
  projectId: string,
  body: { name: string; category?: ProjectStatusCategory },
): Promise<ProjectStatus> {
  return expect(
    http.POST('/organizations/{org_id}/projects/{project_id}/statuses', {
      params: { path: projectPath(orgId, projectId) },
      body,
    }),
  );
}

export function updateProjectStatus(
  orgId: string,
  projectId: string,
  statusId: string,
  body: { name?: string; category?: ProjectStatusCategory; position?: number },
): Promise<ProjectStatus> {
  return expect(
    http.PATCH('/organizations/{org_id}/projects/{project_id}/statuses/{status_id}', {
      params: { path: { ...projectPath(orgId, projectId), status_id: statusId } },
      body,
    }),
  );
}

export function deleteProjectStatus(
  orgId: string,
  projectId: string,
  statusId: string,
): Promise<void> {
  return expectNoContent(
    http.DELETE('/organizations/{org_id}/projects/{project_id}/statuses/{status_id}', {
      params: { path: { ...projectPath(orgId, projectId), status_id: statusId } },
    }),
  );
}

export async function listProjectItems(
  orgId: string,
  projectId: string,
  options: { cursor?: string; q?: string; limit?: number } = {},
): Promise<Paged<ProjectItem>> {
  return paged<ProjectItem>(
    await expect(
      http.GET('/organizations/{org_id}/projects/{project_id}/items', {
        params: {
          path: projectPath(orgId, projectId),
          query: {
            limit: options.limit ?? 100,
            ...(options.q ? { q: options.q } : {}),
            ...at(options.cursor),
          },
        },
      }),
    ),
  );
}

export function addProjectItem(
  orgId: string,
  projectId: string,
  body: { repository_id: string; issue_number?: number; issue_id?: string; status_id?: string },
): Promise<ProjectItem> {
  return expect(
    http.POST('/organizations/{org_id}/projects/{project_id}/items', {
      params: { path: projectPath(orgId, projectId) },
      body,
    }),
  );
}

/**
 * Move an item.
 *
 * The request says where the item should end up — a status, and optionally
 * the item to sit above — and never a position. The board in the browser is a
 * picture of the board as it was when it loaded; the server places the item
 * on the board as it is.
 */
export function moveProjectItem(
  orgId: string,
  projectId: string,
  itemId: string,
  body: { status_id?: string; before_item_id?: string; if_version?: number },
): Promise<ProjectItem> {
  return expect(
    http.PATCH('/organizations/{org_id}/projects/{project_id}/items/{item_id}', {
      params: { path: { ...projectPath(orgId, projectId), item_id: itemId } },
      body,
    }),
  );
}

export function removeProjectItem(orgId: string, projectId: string, itemId: string): Promise<void> {
  return expectNoContent(
    http.DELETE('/organizations/{org_id}/projects/{project_id}/items/{item_id}', {
      params: { path: { ...projectPath(orgId, projectId), item_id: itemId } },
    }),
  );
}

export async function listProjectActivity(
  orgId: string,
  projectId: string,
): Promise<Paged<TimelineEntry>> {
  return paged<TimelineEntry>(
    await expect(
      http.GET('/organizations/{org_id}/projects/{project_id}/activity', {
        params: { path: projectPath(orgId, projectId), query: { limit: 30 } },
      }),
    ),
  );
}

/* ====================================================================== *
 * The inbox, the dashboard, search and the audit log
 * ====================================================================== */

export function markNotificationUnread(orgId: string, notificationId: string): Promise<void> {
  return expectNoContent(
    http.DELETE('/organizations/{org_id}/me/notifications/{notification_id}/read', {
      params: { path: { org_id: orgId, notification_id: notificationId } },
    }),
  );
}

export function acknowledgeAllNotifications(orgId: string): Promise<void> {
  return expectNoContent(
    http.POST('/organizations/{org_id}/me/notifications/read-all', {
      params: { path: { org_id: orgId } },
    }),
  );
}

export async function getNotificationUnreadCount(orgId: string): Promise<number> {
  const result = await expect(
    http.GET('/organizations/{org_id}/me/notifications/unread-count', {
      params: { path: { org_id: orgId } },
    }),
  );
  return result.unread;
}

export async function listNotificationPreferences(
  orgId: string,
): Promise<NotificationPreference[]> {
  return paged<NotificationPreference>(
    await expect(
      http.GET('/organizations/{org_id}/me/notification-preferences', {
        params: { path: { org_id: orgId } },
      }),
    ),
  ).data;
}

export async function updateNotificationPreferences(
  orgId: string,
  preferences: { kind: Schemas['NotificationKind']; channel: 'in_app'; enabled: boolean }[],
): Promise<void> {
  await expect(
    http.PATCH('/organizations/{org_id}/me/notification-preferences', {
      params: { path: { org_id: orgId } },
      body: { preferences },
    }),
  );
}

export function getDashboard(orgId: string): Promise<Dashboard> {
  return expect(
    http.GET('/organizations/{org_id}/me/dashboard', { params: { path: { org_id: orgId } } }),
  );
}

export async function searchOrganization(
  orgId: string,
  query: { q: string; type: SearchType; cursor?: string; limit?: number },
): Promise<Paged<SearchHit>> {
  return paged<SearchHit>(
    await expect(
      http.GET('/organizations/{org_id}/search', {
        params: {
          path: { org_id: orgId },
          query: { q: query.q, type: query.type, limit: query.limit ?? 20, ...at(query.cursor) },
        },
      }),
    ),
  );
}

export interface AuditQuery {
  action?: string;
  actorId?: string;
  outcome?: AuditOutcome;
  cursor?: string;
}

export async function listAuditEvents(
  orgId: string,
  query: AuditQuery = {},
): Promise<Paged<AuditEvent>> {
  return paged<AuditEvent>(
    await expect(
      http.GET('/organizations/{org_id}/audit-events', {
        params: {
          path: { org_id: orgId },
          query: {
            limit: 50,
            ...(query.action ? { action: [query.action] } : {}),
            ...(query.actorId ? { actor_id: query.actorId } : {}),
            ...(query.outcome ? { outcome: query.outcome } : {}),
            ...at(query.cursor),
          },
        },
      }),
    ),
  );
}
